import React, { useState, useEffect } from 'react';
import { AlertTriangle, FileCheck, GitBranch, ArrowRight, TrendingUp, TrendingDown, Shield, ChevronDown, ChevronRight } from 'lucide-react';
import { api } from '../api/client';

export const GovernanceSummaryWidget = ({ 
  className = '', 
  portfolioId = null, 
  programId = null,
  projectId = null
}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    loadData();
  }, [portfolioId, programId, projectId]);

  const loadData = async () => {
    try {
      let entityType = null;
      let entityId = null;
      
      if (projectId) {
        entityType = 'project';
        entityId = projectId;
      } else if (programId) {
        entityType = 'program';
        entityId = programId;
      } else if (portfolioId) {
        entityType = 'portfolio';
        entityId = portfolioId;
      }
      
      const res = await api.getRollupSummary(entityType, entityId);
      setData(res.data?.data);
    } catch (error) {
      console.error('Failed to load governance summary:', error);
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className={`bg-[var(--clr-surface-card)] rounded-lg border border-[var(--clr-border-light)] shadow-[var(--shadow-card)] ${className}`}>
        <div className="p-4">
          <div className="animate-pulse h-6 bg-[var(--clr-surface-subtle)] rounded w-1/3 mb-4"></div>
          <div className="animate-pulse h-20 bg-[var(--clr-surface-subtle)] rounded"></div>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const openIssues = (data.issues?.Open || 0) + (data.issues?.['In Progress'] || 0);
  const pendingDecisions = (data.decisions?.submitted || 0) + (data.decisions?.draft || 0) + (data.decisions?.escalated || 0);
  const openDeps = (data.dependencies?.Open || 0) + (data.dependencies?.['In Progress'] || 0);
  const totalItems = openIssues + pendingDecisions + openDeps;

  return (
    <div className={`bg-[var(--clr-surface-card)] rounded-lg border border-[var(--clr-border-light)] shadow-[var(--shadow-card)] ${className}`} data-testid="governance-summary-widget">
      {/* Header */}
      <button 
        onClick={() => setExpanded(!expanded)}
        className="w-full p-4 flex items-center justify-between hover:bg-[var(--clr-surface-hover)] transition-colors"
      >
        <div className="flex items-center gap-3">
          <Shield className="w-5 h-5 text-[var(--clr-brand-primary)]" />
          <h3 className="text-base font-semibold text-[var(--clr-text-primary)]">Governance Overview</h3>
          {totalItems > 0 && (
            <span className="px-2 py-0.5 text-xs rounded-full bg-[var(--clr-surface-subtle)] text-[var(--clr-text-secondary)]">
              {totalItems} items
            </span>
          )}
        </div>
        <ChevronDown className={`w-4 h-4 text-[var(--clr-text-muted)] transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>

      {expanded && (
        <div className="border-t border-[var(--clr-border-light)] p-4">
          <div className="grid grid-cols-3 gap-4">
            {/* Issues & Risks */}
            <a 
              href="/issues" 
              className="group block p-4 rounded-lg border border-[var(--clr-border-light)] hover:border-[var(--clr-brand-primary)] hover:shadow-[var(--shadow-sm)] transition-all"
            >
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-[var(--clr-surface-subtle)] flex items-center justify-center group-hover:bg-[var(--clr-brand-primary)] group-hover:bg-opacity-10 transition-colors">
                  <AlertTriangle className="w-4 h-4 text-[var(--clr-sev-blocked)]" />
                </div>
                <span className="text-sm font-medium text-[var(--clr-text-secondary)]">Issues & Risks</span>
              </div>
              <p className="text-2xl font-bold text-[var(--clr-text-primary)]">{openIssues}</p>
              <p className="text-xs text-[var(--clr-text-muted)] mt-1">Open / In Progress</p>
              <div className="flex items-center gap-1 mt-2 text-xs text-[var(--clr-brand-primary)] opacity-0 group-hover:opacity-100 transition-opacity">
                <span>View all</span>
                <ChevronRight className="w-3 h-3" />
              </div>
            </a>
            
            {/* Decisions */}
            <a 
              href="/decisions" 
              className="group block p-4 rounded-lg border border-[var(--clr-border-light)] hover:border-[var(--clr-brand-primary)] hover:shadow-[var(--shadow-sm)] transition-all"
            >
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-[var(--clr-surface-subtle)] flex items-center justify-center group-hover:bg-[var(--clr-brand-primary)] group-hover:bg-opacity-10 transition-colors">
                  <FileCheck className="w-4 h-4 text-[var(--clr-sev-atrisk)]" />
                </div>
                <span className="text-sm font-medium text-[var(--clr-text-secondary)]">Decisions</span>
              </div>
              <p className="text-2xl font-bold text-[var(--clr-text-primary)]">{pendingDecisions}</p>
              <p className="text-xs text-[var(--clr-text-muted)] mt-1">Pending Approval</p>
              <div className="flex items-center gap-1 mt-2 text-xs text-[var(--clr-brand-primary)] opacity-0 group-hover:opacity-100 transition-opacity">
                <span>View all</span>
                <ChevronRight className="w-3 h-3" />
              </div>
            </a>
            
            {/* Dependencies */}
            <a 
              href="/dependencies" 
              className="group block p-4 rounded-lg border border-[var(--clr-border-light)] hover:border-[var(--clr-brand-primary)] hover:shadow-[var(--shadow-sm)] transition-all"
            >
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-[var(--clr-surface-subtle)] flex items-center justify-center group-hover:bg-[var(--clr-brand-primary)] group-hover:bg-opacity-10 transition-colors">
                  <GitBranch className="w-4 h-4 text-[var(--clr-brand-primary)]" />
                </div>
                <span className="text-sm font-medium text-[var(--clr-text-secondary)]">Dependencies</span>
              </div>
              <p className="text-2xl font-bold text-[var(--clr-text-primary)]">{openDeps}</p>
              <p className="text-xs text-[var(--clr-text-muted)] mt-1">Open</p>
              <div className="flex items-center gap-1 mt-2 text-xs text-[var(--clr-brand-primary)] opacity-0 group-hover:opacity-100 transition-opacity">
                <span>View all</span>
                <ChevronRight className="w-3 h-3" />
              </div>
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export const EntityHealthWidget = ({ entityType, entityId, entityName, className = '' }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (entityType && entityId) {
      loadData();
    }
  }, [entityType, entityId]);

  const loadData = async () => {
    try {
      const res = await api.getEntityRollup(entityType, entityId);
      setData(res.data?.data);
    } catch (error) {
      console.error('Failed to load entity health:', error);
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className={`bg-[var(--clr-surface-card)] rounded-lg border border-[var(--clr-border-light)] p-4 ${className}`}>
        <div className="animate-pulse h-16 bg-[var(--clr-surface-subtle)] rounded"></div>
      </div>
    );
  }

  if (!data) return null;

  const health = data.health || 'healthy';
  const metrics = data.metrics || {};

  const healthConfig = {
    healthy: { bg: 'bg-[var(--clr-surface-card)]', border: 'border-[var(--clr-sev-healthy)]', text: 'text-[var(--clr-sev-healthy)]', label: 'Healthy' },
    'needs-attention': { bg: 'bg-[var(--clr-surface-card)]', border: 'border-[var(--clr-sev-atrisk)]', text: 'text-[var(--clr-sev-atrisk)]', label: 'Needs Attention' },
    'at-risk': { bg: 'bg-[var(--clr-surface-card)]', border: 'border-[var(--clr-sev-behind)]', text: 'text-[var(--clr-sev-behind)]', label: 'At Risk' },
    blocked: { bg: 'bg-[var(--clr-surface-card)]', border: 'border-[var(--clr-sev-blocked)]', text: 'text-[var(--clr-sev-blocked)]', label: 'Blocked' }
  };

  const config = healthConfig[health] || healthConfig.healthy;

  return (
    <div className={`rounded-lg border-l-4 ${config.bg} ${config.border} p-4 shadow-[var(--shadow-card)] ${className}`} data-testid={`entity-health-${entityType}-${entityId}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-medium text-[var(--clr-text-primary)] capitalize">{entityName || `${entityType} Health`}</span>
        <span className={`text-xs font-medium px-2 py-1 rounded ${config.text} bg-opacity-10`}>{config.label}</span>
      </div>
      <div className="flex items-center gap-4 text-xs text-[var(--clr-text-muted)]">
        {metrics.open_issues > 0 && (
          <div className="flex items-center gap-1">
            <AlertTriangle className="w-3 h-3 text-[var(--clr-sev-blocked)]" />
            <span>{metrics.open_issues} issues</span>
          </div>
        )}
        {metrics.blocking_dependencies > 0 && (
          <div className="flex items-center gap-1">
            <GitBranch className="w-3 h-3 text-[var(--clr-sev-atrisk)]" />
            <span>{metrics.blocking_dependencies} blocking</span>
          </div>
        )}
        {metrics.pending_decisions > 0 && (
          <div className="flex items-center gap-1">
            <FileCheck className="w-3 h-3 text-[var(--clr-brand-primary)]" />
            <span>{metrics.pending_decisions} pending</span>
          </div>
        )}
        {metrics.critical_risks > 0 && (
          <div className="flex items-center gap-1">
            <Shield className="w-3 h-3 text-[var(--clr-sev-blocked)]" />
            <span>{metrics.critical_risks} critical</span>
          </div>
        )}
      </div>
    </div>
  );
};

export const GovernanceMetricsRow = ({ 
  className = '', 
  portfolioId = null, 
  programId = null,
  projectId = null
}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [portfolioId, programId, projectId]);

  const loadData = async () => {
    try {
      let entityType = null;
      let entityId = null;
      
      if (projectId) {
        entityType = 'project';
        entityId = projectId;
      } else if (programId) {
        entityType = 'program';
        entityId = programId;
      } else if (portfolioId) {
        entityType = 'portfolio';
        entityId = portfolioId;
      }
      
      const res = await api.getRollupSummary(entityType, entityId);
      setData(res.data?.data);
    } catch (error) {
      console.error('Failed to load governance metrics:', error);
    }
    setLoading(false);
  };

  if (loading || !data) return null;

  const openIssues = (data.issues?.Open || 0) + (data.issues?.['In Progress'] || 0);
  const pendingDecisions = (data.decisions?.submitted || 0) + (data.decisions?.draft || 0);
  const openDeps = data.dependencies?.Open || 0;

  return (
    <div className={`flex items-center gap-6 ${className}`} data-testid="governance-metrics-row">
      <div className="flex items-center gap-2 text-sm">
        <div className="w-6 h-6 rounded bg-[var(--clr-surface-subtle)] flex items-center justify-center">
          <AlertTriangle className="w-3.5 h-3.5 text-[var(--clr-sev-blocked)]" />
        </div>
        <span className="font-medium text-[var(--clr-text-primary)]">{openIssues}</span>
        <span className="text-[var(--clr-text-muted)]">open issues</span>
      </div>
      <div className="flex items-center gap-2 text-sm">
        <div className="w-6 h-6 rounded bg-[var(--clr-surface-subtle)] flex items-center justify-center">
          <FileCheck className="w-3.5 h-3.5 text-[var(--clr-sev-atrisk)]" />
        </div>
        <span className="font-medium text-[var(--clr-text-primary)]">{pendingDecisions}</span>
        <span className="text-[var(--clr-text-muted)]">pending decisions</span>
      </div>
      <div className="flex items-center gap-2 text-sm">
        <div className="w-6 h-6 rounded bg-[var(--clr-surface-subtle)] flex items-center justify-center">
          <GitBranch className="w-3.5 h-3.5 text-[var(--clr-brand-primary)]" />
        </div>
        <span className="font-medium text-[var(--clr-text-primary)]">{openDeps}</span>
        <span className="text-[var(--clr-text-muted)]">dependencies</span>
      </div>
    </div>
  );
};

export default GovernanceSummaryWidget;
